import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiFetch, ApiError } from '../lib/api';

/**
 * Auth provider. The browser holds no Supabase keys or tokens — the session
 * lives in an HttpOnly cookie set by the Worker. This provider keeps React
 * state in sync with what /api/auth/me reports: the auth user plus their
 * `profiles` row (role + active flag), which drives route gating.
 */

export interface User {
  id: string;
  email?: string;
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
}

export interface Profile {
  user_id: string;
  email: string;
  full_name: string | null;
  role: 'admin' | 'member';
  is_active: boolean;
  last_seen_at: string | null;
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  /** Request a passwordless sign-in email (six-digit code + magic link). */
  sendMagicLink: (email: string) => Promise<void>;
  /** Complete sign-in by submitting the six-digit code from the email. */
  verifyOtp: (email: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadUser() {
    try {
      const { user, profile } = await apiFetch<{ user: User; profile: Profile | null }>(
        '/api/auth/me',
      );
      setUser(user);
      setProfile(profile);
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) {
        // Network or unexpected error — surface so the user knows something's off.
        console.error('Failed to load user', err);
      }
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUser();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      profile,
      loading,
      async sendMagicLink(email) {
        await apiFetch('/api/auth/magic-link', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
      },
      async verifyOtp(email, token) {
        const { user, profile } = await apiFetch<{ user: User; profile: Profile | null }>(
          '/api/auth/verify-otp',
          {
            method: 'POST',
            body: JSON.stringify({ email, token }),
          },
        );
        setUser(user);
        setProfile(profile);
      },
      async signOut() {
        await apiFetch('/api/auth/logout', { method: 'POST' });
        setUser(null);
        setProfile(null);
      },
      refresh: loadUser,
    }),
    [user, profile, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
